import * as signalR from "@microsoft/signalr";
import { ConnectionStatus } from "./ConnectionStatus";
import type {
  ReportUpdateNotification,
  SubscriptionCreatedNotification,
} from "./NotificationTypes";
import type { TicketViewModel } from "./TicketViewModel";

function getWsUri(): string {
  const {
    location: { protocol, hostname, port },
  } = document;

  const signalRPort = port || (protocol === "https:" ? "443" : "80");

  if (hostname === "localhost") {
    return `${protocol}//localhost:${signalRPort}/signalr`;
  }

  return `${protocol}//${hostname}:${signalRPort}/signalr`;
}

interface NotificationEnvelope<T, U extends string> {
  data: T;
  subscriptionId: string;
  notificationType: U;
}

// A union of all possible notification types
type Notification =
  | NotificationEnvelope<ReportUpdateNotification, "ReportUpdateNotification">
  | NotificationEnvelope<
      SubscriptionCreatedNotification,
      "SubscriptionCreatedNotification"
    >;

// Type helper to extract data type from NotificationEnvelope based on notificationType
type ExtractNotificationData<
  TNotification extends Notification,
  TType extends string,
> = TNotification extends NotificationEnvelope<infer TData, TType>
  ? TData
  : never;

type MessageEvent = (event: Notification) => void;

class SignalRManager {
  status = ConnectionStatus.None;

  private _connection: signalR.HubConnection | null = null;
  private _statusListeners: (() => void)[] = [];
  private _connectCalled = false;

  private _messageListeners: MessageEvent[] = [];

  private _updateStatus(status: ConnectionStatus) {
    this.status = status;
    this._emitStatusUpdate();
  }

  private _emitStatusUpdate() {
    for (const listener of this._statusListeners) listener();
  }

  connect(connectingStatus?: ConnectionStatus) {
    if (this._connectCalled) {
      return;
    }
    this._connectCalled = true;

    const uri = getWsUri();

    this._updateStatus(connectingStatus ?? ConnectionStatus.Connecting);

    // Stop any old connections, ignore errors if any, it is only best effort
    void this._connection?.stop().catch(() => {});

    this._connection = new signalR.HubConnectionBuilder()
      .withUrl(uri)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this._connection.on("AnyMessage", (json: string) => {
      // Note: SignalR would normally handle this deserialization, but since we're sending messages with generic types,
      // we need to do it manually here.
      const message = JSON.parse(json) as Notification;

      for (const listener of this._messageListeners) listener(message);
    });

    this._connection.onclose(() => {
      this._updateStatus(ConnectionStatus.Disconnected);
    });

    this._connection.onreconnecting(() => {
      this._updateStatus(ConnectionStatus.Reconnecting);
    });

    this._connection.onreconnected(() => {
      this._updateStatus(ConnectionStatus.Connected);
    });

    this._connection
      .start()
      .then(() => {
        this._updateStatus(ConnectionStatus.Connected);
      })
      .catch((_error) => {
        this._updateStatus(ConnectionStatus.Failed);
      })
      .finally(() => {
        this._connectCalled = false;
      });
  }

  subscribeToStatus(listener: () => void) {
    this._statusListeners.push(listener);

    return () => {
      const index = this._statusListeners.indexOf(listener);
      if (index !== -1) {
        this._statusListeners.splice(index, 1);
      }
    };
  }

  onNotification<
    TType extends Notification["notificationType"],
    TData extends ExtractNotificationData<Notification, TType>,
  >(notificationType: TType, callback: (update: TData) => void): () => void {
    // Create a wrapper listener that filters by notificationType, and casts data
    function listener(message: Notification) {
      if (message.notificationType === notificationType) {
        callback(message.data as TData);
      }
    }

    this._messageListeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this._messageListeners.indexOf(listener);
      if (index !== -1) {
        this._messageListeners.splice(index, 1);
      }
    };
  }

  sendSubscriptionRequest<TSubject extends object>(
    subject: string,
    ticket: TicketViewModel<TSubject>,
  ) {
    const message = {
      subject,
      ticket: ticket.token,
    };
    this._connection?.invoke("Subscribe", message);
  }
}

const signalRManager = new SignalRManager();
export default signalRManager;
